import type { Locale } from '../../server/src/shared/locale';

export type Messages = {
  app: {
    muteSound: string;
    enableSound: string;
    loading: string;
    loadError: string;
    progressUnitMb: string;
  };
  splash: {
    loadingAlt: string;
  };
  menu: {
    closeMenu: string;
    makkah: string;
    freeRoam: string;
    startJourney: string;
    settingsTitle: string;
    soundLabel: string;
    audioTitle: string;
    voiceLabel: string;
    musicLabel: string;
    allLocationsTitle: string;
    sightIconAlt: string;
  };
  assistant: {
    initialMessage: string;
    voiceTranscriptionError: string;
    assistantUnavailable: string;
    continuePrompt: string;
    defaultSightDescription: string;
    sightPrompt: string;
    moreDetailsLabel: string;
    moreDetailsPrompt: string;
    placeholder: string;
    galleryTitle: string;
    status: {
      sending: string;
      thinking: string;
    };
    suggestions: {
      header: string;
      orbit: string;
      info: string;
      moreFacts: string;
      startJourney: string;
      continueStory: string;
      previous: string;
      next: string;
      pauseTour: string;
    };
  };
  chatInput: {
    clearMessage: string;
    openMediaGallery: string;
  };
  loadingText: {
    thinking: string;
  };
  photoViewer: {
    counterSeparator: string;
  };
  sightNavigator: {
    preparingTitle: string;
    preparingDescription: string;
    noDescription: string;
    narrationTitle: string;
    photoHighlight: string;
    mediaStepDescription: string;
    virtualTourStepTitle: string;
    fallbackStepDescription: string;
    sightLabel: string;
    stepLabel: string;
    previous: string;
    next: string;
    keyboardLabel: string;
    keyboardHint: string;
  };
  suggestions: {
    openMenu: string;
  };
  globalMap: {
    markerLabels: Record<string, string>;
  };
  onboarding: {
    title: string;
    hint: string;
  };
  devPanel: {
    title: string;
    sections: {
      locale: string;
      camera: string;
      orbitCenter: string;
      viewOffset: string;
      collision: string;
      devTools: string;
      cameraControls: string;
      pointsOfInterest: string;
      audio: string;
      appConfig: string;
    };
    locale: {
      label: string;
      options: Record<string, string>;
    };
    camera: Record<string, string>;
    hints: {
      camera: Record<string, string>;
      devTools: Record<string, string>;
      poi: Record<string, string>;
    };
    collision: Record<string, string>;
    devTools: Record<string, string>;
    poi: Record<string, string>;
    audio: Record<string, string>;
    musicTrackLabels: Record<string, string>;
    orbitDirection: Record<string, string>;
    viewMode: {
      label: string;
      options: Record<string, string>;
    };
    orbitCenter: Record<string, string>;
    viewOffset: Record<string, string>;
    resetLabel: string;
  };
};

export const messages: Record<Locale, Messages> = {
  en: {
    app: {
      muteSound: 'Mute sound',
      enableSound: 'Turn sound on',
      loading: 'Loading',
      loadError: 'Unable to load',
      progressUnitMb: 'MB',
    },
    splash: {
      loadingAlt: 'Loading…',
    },
    menu: {
      closeMenu: 'Close menu',
      makkah: 'Mekka map',
      freeRoam: 'Free Roam Mode',
      startJourney: 'Start Hajj journey',
      settingsTitle: 'Settings',
      soundLabel: 'Sound',
      audioTitle: 'Audio',
      voiceLabel: 'Voice',
      musicLabel: 'Music',
      allLocationsTitle: 'All locations',
      sightIconAlt: '{name} icon',
    },
    assistant: {
      initialMessage:
        "Hi! I'm your personal AI assistant. Ask me about any sight or ritual, and I will guide you.",
      voiceTranscriptionError: "I didn't catch that. Could you say it again?",
      assistantUnavailable: 'Could not reach the assistant.',
      continuePrompt: 'Please continue guiding me on this topic.',
      defaultSightDescription: '{name} is one of the notable sights here.',
      sightPrompt: 'Tell me about {name}.',
      moreDetailsLabel: 'More details',
      moreDetailsPrompt: 'Tell me more about {name}.',
      placeholder: 'Ask anything',
      galleryTitle: 'Gallery',
      status: {
        sending: 'Sending',
        thinking: 'Thinking',
      },
      suggestions: {
        header: 'I can also tell you:',
        orbit: 'Orbit',
        info: 'Info',
        moreFacts: 'More facts',
        startJourney: 'Start Hajj journey',
        continueStory: 'Continue the story',
        previous: 'Previous',
        next: 'Next',
        pauseTour: 'Pause the tour',
      },
    },
    chatInput: {
      clearMessage: 'Clear message',
      openMediaGallery: 'Open media gallery',
    },
    loadingText: {
      thinking: 'Thinking...',
    },
    photoViewer: {
      counterSeparator: 'of',
    },
    sightNavigator: {
      preparingTitle: 'Preparing scene…',
      preparingDescription: 'Loading sights and calibrating the experience.',
      noDescription: 'No description available',
      narrationTitle: 'Narration',
      photoHighlight: 'Photo highlight',
      mediaStepDescription: 'Media steps currently keep the camera fixed.',
      virtualTourStepTitle: 'Virtual tour step',
      fallbackStepDescription: 'Additional step type – navigation is limited for now.',
      sightLabel: 'Sight {index}',
      stepLabel: 'Step {index}',
      previous: 'Previous',
      next: 'Next',
      keyboardLabel: 'Keyboard:',
      keyboardHint: '←/→ navigate, Home/End pick edges, 1-9 jump to step',
    },
    suggestions: {
      openMenu: 'Open menu',
    },
    globalMap: {
      markerLabels: {
        kaaba: 'Kaaba',
        meeqat: 'Meeqat',
        mina: 'Mina',
        muzdalifah: 'Muzdalifah',
        arafat: 'Arafat',
      },
    },
    onboarding: {
      title: 'Explore the scene',
      hint: 'Rotate it with gestures and tap on the object markers to learn more.',
    },
    devPanel: {
      title: 'Pilgrim Config',
      sections: {
        locale: 'Localization',
        camera: 'Camera',
        orbitCenter: 'Orbit Center',
        viewOffset: 'View Offset',
        collision: 'Collision',
        devTools: 'Dev Tools',
        cameraControls: 'Camera Controls',
        pointsOfInterest: 'Points of Interest',
        audio: 'Audio',
        appConfig: 'App Config',
      },
      locale: {
        label: 'Language',
        options: {
          English: 'en',
          Arabic: 'ar',
        },
      },
      camera: {
        fov: 'Field of View',
        lookSpeedHorizontal: 'Look Speed X',
        lookSpeedVertical: 'Look Speed Y',
        dragInertia: 'Drag Inertia',
        movementSpeed: 'Move Speed',
        orbitAutoMove: 'Auto Orbit',
        orbitAutoMoveDirection: 'Orbit Direction',
        orbitAutoMoveSpeed: 'Orbit Speed',
        orbitMinDistance: 'Orbit Min Dist',
        orbitMaxDistance: 'Orbit Max Dist',
        orbitMinPolarAngle: 'Orbit Min Polar',
        orbitMaxPolarAngle: 'Orbit Max Polar',
        heightCoef: 'Height Mult',
        speedHeightMin: 'Min Height Spd',
        speedHeightMax: 'Max Height Spd',
        pinchMoveSpeed: 'Pinch Speed',
        verticalMin: 'Vertical Min',
        verticalMax: 'Vertical Max',
        clampMinHeight: 'Clamp Min H',
        clampMaxHeight: 'Clamp Max H',
      },
      hints: {
        camera: {
          orbitAutoMove: 'Continuously orbit around the target while in orbit view',
          orbitAutoMoveSpeed: 'Angular speed (radians/sec) when auto-orbit is enabled',
          orbitMinDistance: 'Closest camera distance allowed in orbit view',
          orbitMaxDistance: 'Farthest camera distance allowed in orbit view',
          orbitMinPolarAngle: 'Minimum vertical angle (radians) for orbit',
          orbitMaxPolarAngle: 'Maximum vertical angle (radians) for orbit',
        },
        devTools: {
          showReferenceGrid: 'Toggle the floor/ceiling guides that follow the clamp heights',
          showGlobalMapReferenceGrid: 'Toggle the grid + axes helpers on the global map view',
          showCollisionBounds: 'Outline all collision helpers (green AABBs + hidden FBX volumes)',
          showSightMarkers: 'Display magenta spheres for every sight anchor in the scene',
          showOrbitCenterMarker: 'Render a helper sphere at the orbit pivot to aid debugging',
          showTellerAssistant: 'Render the teller assistant companion model',
          showRearView: 'Display a rear-facing Spark render overlay on the camera',
        },
        poi: {
          showVisibleOnly: 'Only render POI markers if their sight is visible from the camera',
          occlusionProbeMaxOffset: 'Maximum distance to pull the occlusion probe toward the camera',
          occlusionProbeMinDistance: 'Closest allowed distance between the probe and the POI center',
        },
      },
      collision: {
        cameraRadius: 'Camera Radius',
        maxDistance: 'Raycast Range',
        epsilon: 'Epsilon',
      },
      devTools: {
        showReferenceGrid: 'Reference Grid',
        showGlobalMapReferenceGrid: 'Global Map Grid + Axes',
        showCollisionBounds: 'Collision Bounds',
        showSightMarkers: 'Sight Markers',
        showOrbitCenterMarker: 'Orbit Center Marker',
        showTellerAssistant: 'Teller Assistant',
        showRearView: 'Rear View',
      },
      poi: {
        showVisibleOnly: 'Hide Occluded',
        occlusionProbeMaxOffset: 'Probe Offset Max',
        occlusionProbeMinDistance: 'Probe Distance Min',
      },
      audio: {
        soundOn: 'Sound On',
        musicOn: 'Music On',
        voiceOn: 'Voice On',
        voiceVolume: 'Voice Volume',
        assistantVoiceName: 'Assistant Voice',
        musicTrackId: 'Music Track',
        musicVolume: 'Music Volume',
      },
      musicTrackLabels: {
        'crowd-noise-284490': 'Crowd Noise',
        'generic-crowd-background-noise-31310': 'Background Crowd',
        'oud-c-arabian-400521': 'Oud C Arabian',
      },
      orbitDirection: {
        left: 'Left',
        right: 'Right',
      },
      viewMode: {
        label: 'View Mode',
        options: {
          'first-person': 'First-person',
          orbit: 'Orbit',
        },
      },
      orbitCenter: {
        x: 'Center X',
        y: 'Center Y',
        z: 'Center Z',
      },
      viewOffset: {
        enabled: 'Enabled',
        top: 'Top',
        right: 'Right',
        bottom: 'Bottom',
        left: 'Left',
      },
      resetLabel: 'Reset to Defaults',
    },
  },
  ar: {
    app: {
      muteSound: 'كتم الصوت',
      enableSound: 'تشغيل الصوت',
      loading: 'جارٍ التحميل',
      loadError: 'تعذر التحميل',
      progressUnitMb: 'م.ب',
    },
    splash: {
      loadingAlt: 'جارٍ التحميل…',
    },
    menu: {
      closeMenu: 'إغلاق القائمة',
      makkah: 'خريطة مكة',
      freeRoam: 'وضع التجوال الحر',
      startJourney: 'بدء رحلة الحج',
      settingsTitle: 'الإعدادات',
      soundLabel: 'الصوت',
      audioTitle: 'الصوتيات',
      voiceLabel: 'الصوت البشري',
      musicLabel: 'الموسيقى',
      allLocationsTitle: 'جميع المواقع',
      sightIconAlt: 'أيقونة {name}',
    },
    assistant: {
      initialMessage:
        'مرحبًا! أنا مساعدك الذكي. اسألني عن أي معلم أو شعيرة، وسأرشدك.',
      voiceTranscriptionError: 'لم أفهم ذلك. هل يمكنك أن تكرر؟',
      assistantUnavailable: 'تعذر الوصول إلى المساعد.',
      continuePrompt: 'يرجى مواصلة إرشادي في هذا الموضوع.',
      defaultSightDescription: '{name} من أبرز المعالم هنا.',
      sightPrompt: 'أخبرني عن {name}.',
      moreDetailsLabel: 'مزيد من التفاصيل',
      moreDetailsPrompt: 'أخبرني المزيد عن {name}.',
      placeholder: 'اسأل أي شيء',
      galleryTitle: 'المعرض',
      status: {
        sending: 'جارٍ الإرسال',
        thinking: 'جارٍ التفكير',
      },
      suggestions: {
        header: 'يمكنني أيضًا أن أخبرك:',
        orbit: 'دوران',
        info: 'معلومات',
        moreFacts: 'مزيد من المعلومات',
        startJourney: 'بدء رحلة الحج',
        continueStory: 'متابعة القصة',
        previous: 'السابق',
        next: 'التالي',
        pauseTour: 'إيقاف الجولة مؤقتًا',
      },
    },
    chatInput: {
      clearMessage: 'مسح الرسالة',
      openMediaGallery: 'فتح معرض الوسائط',
    },
    loadingText: {
      thinking: 'جارٍ التفكير...',
    },
    photoViewer: {
      counterSeparator: 'من',
    },
    sightNavigator: {
      preparingTitle: 'جارٍ تهيئة المشهد…',
      preparingDescription: 'جارٍ تحميل المعالم وضبط التجربة.',
      noDescription: 'لا يوجد وصف متاح',
      narrationTitle: 'السرد',
      photoHighlight: 'لقطة بارزة',
      mediaStepDescription: 'خطوات الوسائط تُبقي الكاميرا ثابتة حاليًا.',
      virtualTourStepTitle: 'خطوة الجولة الافتراضية',
      fallbackStepDescription: 'نوع خطوة إضافي — التنقل محدود حاليًا.',
      sightLabel: 'المعلم {index}',
      stepLabel: 'الخطوة {index}',
      previous: 'السابق',
      next: 'التالي',
      keyboardLabel: 'لوحة المفاتيح:',
      keyboardHint: '←/→ للتنقل، Home/End لاختيار الأطراف، 1-9 للانتقال إلى الخطوة',
    },
    suggestions: {
      openMenu: 'فتح القائمة',
    },
    globalMap: {
      markerLabels: {
        kaaba: 'الكعبة',
        meeqat: 'الميقات',
        mina: 'منى',
        muzdalifah: 'مزدلفة',
        arafat: 'عرفات',
      },
    },
    onboarding: {
      title: 'استكشف المشهد',
      hint: 'قم بتدويره بالإيماءات وانقر على علامات المعالم لمعرفة المزيد.',
    },
    devPanel: {
      title: 'إعدادات Pilgrim',
      sections: {
        locale: 'اللغة',
        camera: 'الكاميرا',
        orbitCenter: 'مركز الدوران',
        viewOffset: 'إزاحة العرض',
        collision: 'التصادم',
        devTools: 'أدوات التطوير',
        cameraControls: 'تحكمات الكاميرا',
        pointsOfInterest: 'نقاط الاهتمام',
        audio: 'الصوت',
        appConfig: 'إعدادات التطبيق',
      },
      locale: {
        label: 'اللغة',
        options: {
          'الإنجليزية': 'en',
          'العربية': 'ar',
        },
      },
      camera: {
        fov: 'زاوية الرؤية',
        lookSpeedHorizontal: 'سرعة النظر س',
        lookSpeedVertical: 'سرعة النظر ص',
        dragInertia: 'قصور السحب',
        movementSpeed: 'سرعة الحركة',
        orbitAutoMove: 'دوران تلقائي',
        orbitAutoMoveDirection: 'اتجاه الدوران',
        orbitAutoMoveSpeed: 'سرعة الدوران',
        orbitMinDistance: 'أقل مسافة دوران',
        orbitMaxDistance: 'أقصى مسافة دوران',
        orbitMinPolarAngle: 'أدنى زاوية قطبية',
        orbitMaxPolarAngle: 'أقصى زاوية قطبية',
        heightCoef: 'معامل الارتفاع',
        speedHeightMin: 'أدنى سرعة ارتفاع',
        speedHeightMax: 'أقصى سرعة ارتفاع',
        pinchMoveSpeed: 'سرعة القرص',
        verticalMin: 'أدنى عمودي',
        verticalMax: 'أقصى عمودي',
        clampMinHeight: 'أدنى ارتفاع مقيد',
        clampMaxHeight: 'أقصى ارتفاع مقيد',
      },
      hints: {
        camera: {
          orbitAutoMove: 'تدوير الهدف باستمرار في وضع المدار',
          orbitAutoMoveSpeed: 'السرعة الزاوية (راديان/ثانية) عند تفعيل الدوران التلقائي',
          orbitMinDistance: 'أقرب مسافة مسموحة في وضع المدار',
          orbitMaxDistance: 'أبعد مسافة مسموحة في وضع المدار',
          orbitMinPolarAngle: 'أدنى زاوية عمودية (راديان) للمدار',
          orbitMaxPolarAngle: 'أقصى زاوية عمودية (راديان) للمدار',
        },
        devTools: {
          showReferenceGrid: 'تبديل خطوط الأرض/السقف التي تتبع حدود الارتفاع',
          showGlobalMapReferenceGrid: 'تبديل شبكة ومحاور الخريطة العامة',
          showCollisionBounds: 'إظهار حدود جميع أدوات التصادم (مربعات خضراء + مجسمات مخفية)',
          showSightMarkers: 'عرض كرات أرجوانية لكل معلم في المشهد',
          showOrbitCenterMarker: 'عرض كرة مساعدة عند محور الدوران للتصحيح',
          showTellerAssistant: 'عرض نموذج مساعد الراوي',
          showRearView: 'عرض طبقة عرض خلفية للكاميرا',
        },
        poi: {
          showVisibleOnly: 'عرض علامات المعالم فقط إذا كانت مرئية من الكاميرا',
          occlusionProbeMaxOffset: 'أقصى مسافة لسحب مسبار الحجب نحو الكاميرا',
          occlusionProbeMinDistance: 'أقرب مسافة مسموحة بين المسبار ومركز المعلم',
        },
      },
      collision: {
        cameraRadius: 'نصف قطر الكاميرا',
        maxDistance: 'مدى الأشعة',
        epsilon: 'إبسيلون',
      },
      devTools: {
        showReferenceGrid: 'شبكة مرجعية',
        showGlobalMapReferenceGrid: 'شبكة الخريطة + المحاور',
        showCollisionBounds: 'حدود التصادم',
        showSightMarkers: 'علامات المعالم',
        showOrbitCenterMarker: 'علامة مركز الدوران',
        showTellerAssistant: 'مساعد الراوي',
        showRearView: 'المنظر الخلفي',
      },
      poi: {
        showVisibleOnly: 'إخفاء المحجوب',
        occlusionProbeMaxOffset: 'أقصى إزاحة للمسبار',
        occlusionProbeMinDistance: 'أدنى مسافة للمسبار',
      },
      audio: {
        soundOn: 'تشغيل الصوت',
        musicOn: 'تشغيل الموسيقى',
        voiceOn: 'تشغيل الصوت البشري',
        voiceVolume: 'مستوى الصوت البشري',
        assistantVoiceName: 'صوت المساعد',
        musicTrackId: 'المسار الموسيقي',
        musicVolume: 'مستوى الموسيقى',
      },
      musicTrackLabels: {
        'crowd-noise-284490': 'ضجيج الحشود',
        'generic-crowd-background-noise-31310': 'ضجيج الخلفية',
        'oud-c-arabian-400521': 'عود عربي',
      },
      orbitDirection: {
        left: 'يسار',
        right: 'يمين',
      },
      viewMode: {
        label: 'نمط العرض',
        options: {
          'first-person': 'من منظور الشخص',
          orbit: 'مدار',
        },
      },
      orbitCenter: {
        x: 'المركز س',
        y: 'المركز ص',
        z: 'المركز ع',
      },
      viewOffset: {
        enabled: 'مفعل',
        top: 'أعلى',
        right: 'يمين',
        bottom: 'أسفل',
        left: 'يسار',
      },
      resetLabel: 'إعادة التعيين للافتراضي',
    },
  },
};
